import type { QueryClient } from "@tanstack/react-query";
import { identityHubKeys } from "@/lib/queries";
import { auditLogKeys } from "@/features/identity-hub/lib/queries/audit-log.queries";
import type {
  AccessGroupDetail,
  AccessGroupItem,
  UserAccessEntry,
} from "@/features/identity-hub/api/identity-hub.api";

export type PermissionTuple = { type: string; method: string; route: string };

/**
 * Every access-group tenant list is cached under two keys — the active-only
 * view and the `includeArchived: true` view. Both need the same patch
 * whenever a group changes, so this is the single place that touches both.
 */
export function patchAccessByTenantCaches(
  qc: QueryClient,
  tenantId: string,
  updater: (old: AccessGroupItem[] | undefined) => AccessGroupItem[] | undefined,
): void {
  qc.setQueryData(identityHubKeys.accessByTenant(tenantId), updater);
  qc.setQueryData(identityHubKeys.accessByTenant(tenantId, { includeArchived: true }), updater);
  qc.setQueriesData<Record<string, AccessGroupItem[]>>(
    { queryKey: identityHubKeys.accessByTenantsPrefix() },
    (old) => {
      if (!old || !(tenantId in old)) return old;
      const updated = updater(old[tenantId]);
      if (updated === undefined) return old;
      return { ...old, [tenantId]: updated };
    },
  );
}

/** Patch IAM caches after a role's permissions change — avoids refetching the full dashboard. */
export function patchAccessPermissionsInCache(
  qc: QueryClient,
  params: { accessId: string; tenantId: string; permissions: PermissionTuple[] },
): void {
  const { accessId, tenantId, permissions } = params;
  const updatedAt = new Date().toISOString();

  qc.setQueryData<AccessGroupDetail>(identityHubKeys.access(accessId), (old) => {
    if (!old) return old;
    return { ...old, permissions };
  });

  patchAccessByTenantCaches(qc, tenantId, (old) =>
    old?.map((g) => (g.id === accessId ? { ...g, permissions, updatedAt } : g)),
  );

  const patchUserAccessEntries = (old: UserAccessEntry[] | undefined) => {
    if (!old) return old;
    let changed = false;
    const next = old.map((entry) => {
      if (entry.accessId !== accessId) return entry;
      changed = true;
      return {
        ...entry,
        access: { ...entry.access, permissions },
      };
    });
    return changed ? next : old;
  };

  qc.setQueriesData<UserAccessEntry[]>(
    { queryKey: identityHubKeys.userAccessPrefix() },
    patchUserAccessEntries,
  );

  qc.setQueriesData<Record<string, UserAccessEntry[]>>(
    { queryKey: identityHubKeys.usersAccessPrefix() },
    (old) => {
      if (!old) return old;
      let changed = false;
      const next: Record<string, UserAccessEntry[]> = {};
      for (const [userId, entries] of Object.entries(old)) {
        const patched = patchUserAccessEntries(entries);
        if (patched !== entries) changed = true;
        next[userId] = patched ?? entries;
      }
      return changed ? next : old;
    },
  );

  void qc.invalidateQueries({ queryKey: auditLogKeys.access(accessId) });
}
